"use client";

import { useState } from "react";
import { Bot, Check, Loader2 } from "lucide-react";
import { SegmentedToggle } from "@/components/shared/SegmentedToggle";
import { ComingSoonButton } from "@/features/billing/components/ComingSoonButton";
import { useBilling } from "@/features/billing/hooks/useBilling";
import { comingSoonFor } from "@/features/billing/lib/planCta";
import { usePricingModal } from "@/features/auth/hooks/usePricingModal";
import { useUser } from "@/features/auth/hooks/useUser";
import {
  API_LIMITS,
  API_UNIT_COSTS,
  PLAN_CONFIG,
  intervalSuffix,
  yearlySavingsPercent,
  type BillingInterval,
} from "@/lib/billing/planConfig";
import { formatInt } from "../lib/format";
import { API_ACCENT, BTN_PRIMARY, BTN_SECONDARY, CARD } from "./agenticStyle";

const STUDIO = PLAN_CONFIG.studio;

const INCLUDED = [
  `${formatInt(API_LIMITS.unitsPerMonth)} API units a month (${formatInt(API_LIMITS.unitsPerDay)} a day): about ${formatInt(API_LIMITS.unitsPerMonth / API_UNIT_COSTS.lookup)} lookups or ${formatInt(API_LIMITS.unitsPerMonth / API_UNIT_COSTS.series)} history series`,
  "A hosted MCP server for Claude, Cursor, VS Code and any MCP client",
  "A REST API with an OpenAPI 3.1 spec and a reference generated from the code",
  "Every game, chart, release and publisher, with all-time history",
  `Up to ${API_LIMITS.maxActiveKeys} keys, with usage per key and per endpoint`,
  `Everything else in Studio: ${STUDIO.aiCreditsPerMonth} AI credits and ${STUDIO.audienceScansPerMonth} audience scans a month, and all of Pro`,
];

/** BTN_PRIMARY's look without its hover, for the coming-soon stand-in. */
const BTN_PRIMARY_STILL =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-[#66c0f4] px-4 py-2 text-sm font-semibold text-[#0d151e]";

/**
 * API access is part of Studio: this is what it gets you, for a viewer
 * without it. The buttons go through useBilling().choosePlan like every other
 * plan button: sign-in first for a signed-out viewer, a checkout, or the
 * in-app upgrade for a Pro subscriber. While Studio isn't on sale the button
 * is a disabled "Coming soon" stand-in with no handler (isPlanForSale).
 */
export function ApiAccessPitch({ signedIn }: { signedIn: boolean }) {
  const { choosePlan, pendingCheckout, viewer } = useBilling();
  const { plan } = useUser();
  const pricing = usePricingModal();
  const [interval, setInterval] = useState<BillingInterval>("month");
  const [error, setError] = useState<string | null>(null);
  const price = STUDIO.prices[interval];
  const busy = pendingCheckout === "studio";
  const label = !signedIn ? `Sign in to get ${STUDIO.label}` : plan === "pro" ? `Upgrade to ${STUDIO.label}` : `Get ${STUDIO.label}`;

  async function get() {
    setError(null);
    const r = await choosePlan("studio", interval, "agentic_page");
    if (!r.ok) setError(r.message);
  }

  return (
    <section className={CARD} aria-labelledby="api-access-heading">
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="max-w-xl">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: "rgba(45,212,191,0.12)" }}>
              <Bot className="h-[18px] w-[18px]" style={{ color: API_ACCENT }} aria-hidden />
            </span>
            <h2 id="api-access-heading" className="text-lg font-semibold text-white">
              API access comes with {STUDIO.label}
            </h2>
          </div>
          <p className="mt-2 text-sm text-[#c7d5e0]">
            Keys for your scripts and AI agents, on the same data as the site.
          </p>
          <ul className="mt-4 space-y-2">
            {INCLUDED.map((line) => (
              <li key={line} className="flex items-start gap-2 text-sm text-[#c7d5e0]">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: API_ACCENT }} aria-hidden />
                {line}
              </li>
            ))}
          </ul>
        </div>
        <div className="w-full rounded-lg border border-[#2a475e]/70 bg-[#16202d] p-4 md:w-72">
          <SegmentedToggle
            options={[
              { value: "month", label: "Monthly" },
              { value: "year", label: `Yearly −${yearlySavingsPercent("studio")}%` },
            ]}
            value={interval}
            onChange={setInterval}
            ariaLabel="Billing interval"
          />
          <p className="mt-4 text-3xl font-bold text-white">
            {price.display}
            <span className="ml-1 text-sm font-medium text-[#8f98a0]">{intervalSuffix(interval)}</span>
          </p>
          <p className="mt-1 text-xs text-[#8f98a0]">{STUDIO.label}, plus applicable tax.</p>
          {comingSoonFor(viewer, "studio") ? (
            <div className="mt-4">
              <ComingSoonButton className={`${BTN_PRIMARY_STILL} w-full`}>{label}</ComingSoonButton>
            </div>
          ) : (
            <button type="button" onClick={get} className={`${BTN_PRIMARY} mt-4 w-full`} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {label}
            </button>
          )}
          <button
            type="button"
            onClick={() => pricing.open("api_access", { highlight: "studio" })}
            className={`${BTN_SECONDARY} mt-2 w-full`}
          >
            Compare plans
          </button>
          {error && (
            <p className="mt-2 text-xs text-red-300" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
